import {
  Link,
  Outlet,
  createFileRoute,
  useMatches,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  getInfoPageTo,
  sidebarInfoPages,
} from "@modules/shop/config/info-pages";
import { useGetPageByHandleQuery } from "@modules/shopify/hooks";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

export const Route = createFileRoute("/$locale/_layout/(public)/_public/info")({
  component: InfoLayout,
});

function InfoLayout() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();
  const localeVars = getCountryVariables(locale);
  const { data: aboutUsData } = useGetPageByHandleQuery({
    handle: "about-us",
    ...localeVars,
  });
  const { data: paymentOptionsData } = useGetPageByHandleQuery({
    handle: "payment-options",
    ...localeVars,
  });
  const { data: faqData } = useGetPageByHandleQuery({
    handle: "faq",
    ...localeVars,
  });
  const matches = useMatches();
  // Get current segment from URL
  const currentPath = matches[matches.length - 1]?.pathname ?? "";
  const currentSegment = currentPath.split("/").pop();

  // Pages missing here (withdrawal-form, complaints, cookies) use their footer label.
  const pageTitlesByHandle: Partial<Record<string, string>> = {
    "about-us": aboutUsData?.page?.title,
    "payment-options": paymentOptionsData?.page?.title,
    faq: faqData?.page?.title,
  };

  const sidebarGroups = [
    {
      key: "information",
      pages: sidebarInfoPages.filter((page) => page.footerGroup !== "legal"),
    },
    {
      key: "legal",
      pages: sidebarInfoPages.filter((page) => page.footerGroup === "legal"),
    },
  ];

  const getSidebarTitle = (page: (typeof sidebarInfoPages)[number]) => {
    if (page.source !== "page") {
      return t(`Footer.${page.footerKey}`);
    }

    return pageTitlesByHandle[page.handle] ?? t(`Footer.${page.footerKey}`);
  };

  return (
    <div className="flex flex-1 flex-col animate-fadeIn">
      <div className="grid flex-1 grid-cols-40-dynamic max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:pb-10 max-mobile:pt-6">
        {/* Back button - mobile only */}
        <div className="col-span-padded mobile:hidden">
          <Link
            to="/$locale"
            params={{ locale }}
            className="text-text-3 hover:text-text-1"
          >
            ← {t("Common.back")}
          </Link>
        </div>

        {/* Sidebar Navigation */}
        <div className="col-span-padded max-md:!col-span-full max-md:h-fit max-md:p-0 max-mobile:overflow-x-auto no-scrollbar mobile:!col-[1/8] mobile:min-w-[220px] mobile:border-r mobile:border-border-5 mobile:px-10 mobile:py-[60px] laptop:!col-[2/8] laptop:pl-0">
          <nav className="sticky top-[60px] flex w-full min-w-0 flex-col font-primary text-small max-mobile:w-max max-mobile:flex-row max-mobile:gap-5 max-md:px-[calc(100%/15)]">
            {sidebarGroups.map((group) => (
              <div
                key={group.key}
                className="max-mobile:contents mobile:mt-8 mobile:first:mt-0"
              >
                <p className="mb-1 pl-[18px] text-tny font-medium tracking-wider text-text-3 uppercase max-mobile:hidden">
                  {t(`StaticPages.groups.${group.key}`)}
                </p>
                {group.pages.map((page) => (
                  <Link
                    key={page.path}
                    to={getInfoPageTo(page.path)}
                    params={{ locale }}
                    className={`block min-w-0 py-2.5 [overflow-wrap:anywhere] transition-colors duration-200 max-mobile:whitespace-nowrap mobile:border-l-2 mobile:pl-4 ${
                      currentSegment === page.path
                        ? "font-bold text-primary mobile:border-primary"
                        : "text-text-2 hover:text-text-1 focus-visible:text-text-1 mobile:border-transparent"
                    }`}
                  >
                    {getSidebarTitle(page)}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </div>

        {/* Page Content */}
        <Outlet />
      </div>
    </div>
  );
}
